import type { MispricedGroup, MispricedSignal, MispricedSlice } from "../api/types";

/**
 * Разбор теневого сигнала «расхождение модели и стакана» для экрана.
 *
 * # Что этот раздел обязан сказать
 *
 * На карточке рынка модель стоит рядом с серединой стакана, и 2026-08-27
 * расхождение там было в десять центов при комиссии в девять десятых. Вопрос,
 * ради которого подсистема заведена, ровно один: НАСТОЯЩЕЕ ли оно.
 *
 * Отсюда три пары чисел, и каждая отвечает на свою половину вопроса:
 *
 *   - кромка на экране против кромки по цене НАЛИВА. Первая считается против
 *     середины, а платят аск и комиссию; разница — половина спреда со ставкой,
 *     и на пятиминутках она бывает больше самой кромки;
 *   - выставлено против НАЛИТО. Мейкерская заявка стоит бесплатно и исполняется
 *     редко: «сигналов много» читается как «сделок много», если не показать оба;
 *   - угадано среди ВСЕХ против угаданного среди ИСПОЛНИВШИХСЯ. Разница между
 *     ними и есть неблагоприятный отбор — мейкера наливают охотнее там, где он
 *     неправ.
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Имя источника словами: `pm` и `model` ничего не говорят стороннему глазу. */
export function sourceLabel(source: string): string {
  if (source === "pm") return "ряд биржи";
  if (source === "model") return "наша модель";
  return source;
}

/** Имя роли словами. Те же слова, что на кнопках быстрых действий. */
export function roleLabel(role: string): string {
  if (role === "maker") return "мейкер";
  if (role === "taker") return "тейкер";
  return role;
}

/** Кромка со знаком и тремя знаками после запятой. Прочерк — «не считали». */
export function edge(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.abs(n).toFixed(3)}`;
}

/**
 * Сколько кромки съедает спред с комиссией: медиана на экране минус медиана по
 * цене налива. null — сравнивать не с чем.
 *
 * Число, которое либо оправдывает затею, либо обнуляет её целиком, и потому оно
 * стоит в приговоре раздела, а не в сноске.
 */
export function spreadCost(g: MispricedGroup): number | null {
  const mid = num(g.edge_mid_median);
  const exec = num(g.edge_median);
  if (mid === null || exec === null) return null;
  return mid - exec;
}

/** Доля, посчитанная только при непустом знаменателе. Прочерк — «не из чего». */
export function ratio(ok: number, n: number): string {
  if (n <= 0) return "—";
  return `${Math.round((100 * ok) / n)} %`;
}

/**
 * Отравление отбором: угадано среди исполнившихся минус угадано среди всех, в
 * процентных пунктах. null — знаменателя нет.
 *
 * Отрицательное значение и есть подпись неблагоприятного отбора: нас наливают
 * охотнее там, где мы неправы. Разбор копирования нашёл это косвенно
 * (p = 0.002); здесь оно считается прямо.
 */
export function adverse(g: MispricedGroup): number | null {
  if (g.settled === 0 || g.filled === 0) return null;
  return (100 * g.won_filled) / g.filled - (100 * g.won) / g.settled;
}

/**
 * Счёт со знаменателем: «−1.25 по 9».
 *
 * Знаменатель обязателен. Ноль в счёте бывает измеренный («заявка стояла, никто
 * не налил») и пустой («считать было нечем»), и сумма их складывает — без
 * знаменателя пустое измерение выглядит как измеренный ноль.
 *
 * Принимает ровно пару «счёт и знаменатель», а не всю группу: то же слово нужно
 * корзине разреза, и требовать ради двух полей целую сводку значило бы завести
 * вторую функцию с тем же телом (тот же приём, что у marketWord).
 */
export function pnlWord(g: { pnl: string; priced: number }): string {
  if (g.priced === 0) return "не считан";
  const n = num(g.pnl);
  if (n === null) return "не считан";
  return `${n >= 0 ? "+" : "−"}$${Math.abs(n).toFixed(2)} по ${g.priced}`;
}

/** Жизнь заявки в секундах. */
export function live(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || ms === 0) return "—";
  return `${(ms / 1000).toFixed(0)} с`;
}

/** Фора до конца окна, секунды. Знаковая: рынок открыт и после конца. */
export function lead(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return `${(ms / 1000).toFixed(0)} с`;
}

/** Причина снятия словами. Пусто — заявка стояла до конца наблюдения. */
export function cancelWord(reason: string | null | undefined): string {
  switch (reason) {
    case "edge":
      return "расхождение кончилось";
    case "book":
      return "стакан пропал";
    case "window":
      return "кончилось наблюдение";
    case "settled":
      return "исход определился";
    case undefined:
    case null:
    case "":
      return "стояла до конца";
    default:
      return reason;
  }
}

/**
 * Корзины одной пары по одной оси, в порядке чтения.
 *
 * Минуты идут ходом окна — от ранних заходов к поздним. Номер корзины считает
 * ОТ КОНЦА окна (длительность в журнале не хранится, а решает здесь близость к
 * резолву), поэтому ход окна — это убывание номера, и заходы после конца
 * закрывают список. Цены идут лесенкой вверх, как ступени у смещённого края.
 */
export function slicesFor(
  slices: MispricedSlice[],
  source: string,
  role: string,
  cut: MispricedSlice["cut"],
): MispricedSlice[] {
  return slices
    .filter((s) => s.source === source && s.role === role && s.cut === cut)
    .sort((a, b) => (cut === "minute" ? b.bucket - a.bucket : a.bucket - b.bucket));
}

/**
 * Подпись корзины разреза.
 *
 * У минуты — интервалом до конца окна, а не порядковым номером: «м2» читался бы
 * как вторая минута ОТ НАЧАЛА, по образцу смещённого края, а номер здесь
 * считает от конца. Нулевая корзина — заход после конца окна: рынок остаётся
 * открытым, и такие строки законны, а не мусор.
 */
export function sliceLabel(s: MispricedSlice): string {
  if (s.cut === "price") {
    return `${(s.bucket / 10).toFixed(1)}–${((s.bucket + 1) / 10).toFixed(1)}`;
  }
  if (s.bucket <= 0) return "после конца окна";
  return `за ${s.bucket - 1}–${s.bucket} мин до конца`;
}

/**
 * Приговор разделу одной строкой.
 *
 * Пишется от ЦЕНЫ НАЛИВА, а не от показанной кромки: показанная — это то, что
 * видно, а не то, что достаётся. Ровно эту подмену раздел и обязан снимать.
 */
export function verdict(groups: MispricedGroup[]): string {
  if (groups.length === 0 || groups.every((g) => g.signals === 0)) {
    return "расхождений ещё не было";
  }
  const parts = groups
    .filter((g) => g.signals > 0)
    .map((g) => {
      const cost = spreadCost(g);
      const costWord =
        cost === null ? "" : `, спред съедает ${cost.toFixed(3)}`;
      return (
        `${sourceLabel(g.source)}/${roleLabel(g.role)}: ${g.signals} заявок, ` +
        `кромка ${edge(g.edge_median)}${costWord}`
      );
    });
  return parts.join("; ");
}

/**
 * Цвет строки по кромке ПО ЦЕНЕ НАЛИВА.
 *
 * Ноль отдельно от «нет данных» намеренно: приглушённый ноль читается как
 * «сошлось», а пустое место — как «не считали».
 */
export function tone(v: string | null | undefined): "up" | "down" | "flat" {
  const n = num(v);
  if (n === null || n === 0) return "flat";
  return n > 0 ? "up" : "down";
}

/**
 * Короткое имя рынка для ленты: актив и конец окна.
 *
 * Принимает НАРУЖНУЮ форму, а не строку журнала: то же имя нужно и группе, и
 * отдельной заявке, а требовать ради двух полей целую строку значило бы завести
 * вторую функцию с тем же телом.
 */
export function marketWord(s: { asset?: string | undefined; window_end?: string; windowEnd?: string }): string {
  const raw = s.window_end ?? s.windowEnd;
  if (raw === undefined) return s.asset ?? "?";
  const end = new Date(raw);
  const hh = String(end.getUTCHours()).padStart(2, "0");
  const mm = String(end.getUTCMinutes()).padStart(2, "0");
  return `${s.asset ?? "?"} ${hh}:${mm}`;
}

/**
 * Одна строка ленты: ОБЕ роли по одной стороне одного окна.
 *
 * # Почему группировка, а не строка на заявку
 *
 * В журнале единица — заявка, и это верно: тейкер и мейкер входят в разные
 * моменты и разными ценами, а мейкер ещё и переставляется. Но читают ленту не
 * по заявкам, а по РЕШЕНИЯМ: «модель разошлась со стаканом по этой стороне —
 * что дала каждая роль». Ровно так устроена лента почти решённого, и по тому же
 * доводу: отказ одной из ролей есть половина ответа, а не пустота.
 *
 * Мейкер берётся ПОСЛЕДНЕЙ попыткой, а их число едет рядом: строка отвечает на
 * «чем кончилось», а полная история попыток лежит в журнале и в ноте
 * `mispriced_replaced_total`.
 */
export interface MispricedEntry {
  key: string;
  /** Момент РЕШЕНИЯ: самая ранняя заявка группы — с неё всё началось. */
  detectedAt: string;
  source: string;
  /** undefined явно, а не необязательное поле: строка сборки всегда его задаёт. */
  asset: string | undefined;
  windowEnd: string;
  outcome: string;
  /** Справедливая цена стороны. Берётся у тейкера, а при его отсутствии у мейкера. */
  fair: string;
  leadMs: number;
  maker: MispricedSignal | undefined;
  /** Сколько раз мейкер вставал заново. Единица — переставлений не было. */
  makerAttempts: number;
  taker: MispricedSignal | undefined;
}

/** Группирует ленту по (окно, источник, сторона), свежие сверху. */
export function entriesOf(signals: MispricedSignal[]): MispricedEntry[] {
  const byKey = new Map<string, MispricedSignal[]>();
  for (const s of signals) {
    const key = `${s.condition_id}|${s.source}|${s.asset_id}`;
    const list = byKey.get(key);
    if (list) list.push(s);
    else byKey.set(key, [s]);
  }

  const out: MispricedEntry[] = [];
  for (const [key, list] of byKey) {
    const makers = list
      .filter((s) => s.role === "maker")
      .sort((a, b) => a.attempt - b.attempt);
    const taker = list.find((s) => s.role === "taker");
    // Последняя попытка мейкера — та, чем дело кончилось.
    const maker = makers.length > 0 ? makers[makers.length - 1] : undefined;
    const any = taker ?? maker ?? list[0];
    if (!any) continue;
    const earliest = list.reduce((a, b) => (a.detected_at <= b.detected_at ? a : b));
    out.push({
      key,
      detectedAt: earliest.detected_at,
      source: any.source,
      asset: any.asset,
      windowEnd: any.window_end,
      outcome: any.outcome_name,
      fair: any.fair,
      leadMs: earliest.lead_ms,
      maker,
      makerAttempts: makers.length,
      taker,
    });
  }
  return out.sort((a, b) => (a.detectedAt < b.detectedAt ? 1 : -1));
}

/**
 * Роль одной строкой: цена и кромка сверху, подробность снизу.
 *
 * «не было» — это ОТВЕТ, а не пустота: у тейкера он означает «в стакане не
 * нашлось ничего дешевле потолка», у мейкера — «наша цена перекрыла бы стакан
 * либо оказалась ниже лучшей покупки». Половина ответа, и терять её нельзя.
 */
export function roleLine(s: MispricedSignal | undefined, attempts = 0,
): { head: string; note: string } {
  if (!s) return { head: "не было", note: "" };
  const head = `${s.plan.price ?? "—"} · ${edge(s.edge_exec)}`;
  if (s.role === "taker") {
    return { head, note: `${s.plan.size ?? "—"} share` };
  }
  const parts: string[] = [];
  parts.push(s.fill === undefined ? "налито —" : `налито ${s.fill}`);
  if (attempts > 1) parts.push(`попытка ${s.attempt}`);
  if (s.cancel_reason) parts.push(cancelWord(s.cancel_reason));
  return { head, note: parts.join(", ") };
}

/**
 * Чем кончилось: исход и счёт по обеим ролям.
 *
 * Счёт по ролям ПОРОЗНЬ, а не суммой: тейкер платит комиссию и исполняется
 * наверняка, мейкер не платит и исполняется редко — сумма их не описывает ни
 * одного.
 */
export function entryOutcome(e: MispricedEntry): string {
  const rows = [e.taker, e.maker].filter(Boolean) as MispricedSignal[];
  if (rows.length === 0) return "—";
  if (!rows.some((r) => r.settled_at)) return "ждём исход";
  const won = rows.some((r) => r.resolved && r.won);
  const scored = rows
    .filter((r) => r.shadow_pnl !== undefined)
    .map((r) => `${r.role === "taker" ? "Т" : "М"} ${r.shadow_pnl}`);
  const head = rows.some((r) => r.resolved) ? (won ? "угадано" : "мимо") : "исход не подтверждён";
  return scored.length > 0 ? `${head}: ${scored.join(", ")}` : head;
}
